// src/data/javascript.js

export const javascriptNotes = [
    {
      id: 301,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Introduction to JavaScript",
      description:
        "Learn about the origin of JavaScript, its creator, and its primary execution environments: browsers and Node.js.",
      content: `
##  History & Developer Info
 **Creator:** **Brendan Eich** (Netscape Communications).
 **Year Created:** **1995** (originally named Mocha, then LiveScript, finally JavaScript).
 **Purpose:** To make web pages interactive and dynamic (client-side validation, manipulation).
##  JavaScript in Browsers & Node.js
### 1. Web Browsers (Client-Side)
* The primary environment. Code is executed by the browser's JavaScript engine (e.g., V8 in Chrome, SpiderMonkey in Firefox).
* Allows interaction with the Document Object Model (DOM) to modify web pages.
### 2. Node.js (Server-Side)
* A runtime environment that allows executing JavaScript code outside of a browser.
* Used for building scalable server-side applications, command-line tools, and backend APIs.
* Leverages the same V8 engine as Chrome.
##  Setting up the Environment
 **Browser Console:** Best for small snippets, immediate testing, and debugging frontend code.
 **VS Code:** A powerful text editor with excellent JavaScript support (syntax highlighting, IntelliSense, debugging).
 **Node.js:** Essential for running server-side code and managing packages using npm.
        `,
      tags: ["JavaScript", "Introduction", "History", "Node.js", "Browser"],
      date: "2024-10-23",
    },
    {
      id: 302,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Data Types and Variables",
      description:
        "Understanding variable declaration keywords and the fundamental primitive and reference data types in JavaScript.",
      content: `
##  Data Types
JavaScript has two main categories of data types:
### Primitive Types (Value Types)
A variable stores the actual value.
1.  **string:** Textual data (\`"Hello"\`).
2.  **number:** Both integers and floating-point numbers (\`10\`, \`3.14\`).
3.  **boolean:** Logical entity (\`true\` or \`false\`).
4.  **null:** Intentional absence of any object value.
5.  **undefined:** A variable that has been declared but not assigned a value.
6.  **symbol:** Unique and immutable identifier (ES6).
### Reference Types (Object Types)
A variable stores a reference (memory address) to the value.
 **object:** Keyed collections of data (\`{ name: "A" }\`).
 **array:** Ordered lists of data (\`[1, 2, 3]\`).
 **function:** Reusable blocks of code.
##  Variable Declaration Keywords
| Keyword | Scope | Reassignment | Redeclaration | Hoisting |
| :--- | :--- | :--- | :--- | :--- |
| \`var\` | Function | Yes | Yes | Yes (initialized with \`undefined\`) |
| \`let\` | Block | Yes | No | Yes (but uninitialized, causes Temporal Dead Zone) |
| \`const\` | Block | No | No | Yes (but uninitialized, causes Temporal Dead Zone) |
 **Best Practice:** Always prefer **\`const\`**. Use **\`let\`** only when the value absolutely needs to change. Avoid **\`var\`**.
        `,
      tags: ["JavaScript", "Basics", "Data Types", "Variables", "let", "const"],
      date: "2024-10-23",
    },
    {
      id: 303,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Operators",
      description:
        "A guide to the main operator types used for calculations, assignments, comparisons, and logical operations.",
      content: `
##  Operators in JavaScript
### 1. Arithmetic Operators
Used for mathematical calculations.
 * \`+\`, \`-\`, \`*\`, \`/\` (Addition,  Subtraction, Multiplication, Division)
  * \`%\` (Remainder)
 * \`**\` (Exponentiation)
 * \`++\` (Increment), \`--\` (Decrement)
### 2. Assignment Operators
Used to assign values to variables.
 * \`=\` (Assignment)
 * \`+=\`, \`-=\`, \`*=\` (Shorthand assignment: \`x += y\` is the same as \`x = x + y\`).
### 3. Comparison Operators
Used to compare two values, returning a boolean (\`true\` or \`false\`).
* \`==\` (Loose equality, ignores type: \`5 == '5'\` is \`true\`).
* \`!=\`(Loose inequality).
* \`===\` (Strict equality, checks value and type: \`5 === '5'\` is \`false\`). Always prefer strict equality.
* \`!==\` (Strict inequality).
* \`>\`,\`<\`, \`>=\`, \`<=\` (Greater/Less than).
### 4. Logical Operators
Used to combine boolean expressions.
* \`&&\` (AND): Returns true if both operands are true.
 *  (OR): Returns true if at least one operand is true.
* \`!\` (NOT): Inverts the boolean value.
### 5. Ternary Operator (Conditional Operator)
A shorthand for a simple \`if-else\` statement.
\`\`\`javascript
const age = 20;
const status = (age >= 18) ? 'Adult' : 'Minor'; // status is 'Adult'
\`\`\`
        `,
      tags: ["JavaScript", "Basics", "Operators", "Ternary"],
      date: "2024-10-23",
    },
    {
      id: 304,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Conditional Statements",
      description:
        "Control the flow of execution in your program using conditional logic.",
      content: `
##  Conditional Statements
### 1. \`if\`, \`if-else\`, and Nested \`if\`
Used to execute a block of code only if a specified condition evaluates to \`true\`.
\`\`\`javascript
let temperature = 25;

if (temperature > 30) {
  console.log("It's hot.");
} else if (temperature > 20) { // else if for multiple conditions
  console.log("It's warm.");
} else { // default case
  console.log("It's cool.");
}
\`\`\`
### 2. \`switch-case\`
Used for testing a single variable against multiple possible values (cases). It is generally cleaner than many nested \`if-else if\` blocks.
* The \`break\` keyword is crucial to stop execution from "falling through" to the next case.
* The \`default\` keyword is optional and executes if no case matches.
\`\`\`javascript
let day = 'Mon';

switch (day) {
case 'Sat':
case 'Sun': // Fall-through for multiple cases
  console.log('Weekend');
  break;
 case 'Mon':
 console.log('Start of week');
  break;
 default:
  console.log('Weekday');
}
\`\`\`
        `,
      tags: ["JavaScript", "Control Flow", "if", "else", "switch"],
      date: "2024-10-24",
    },
    {
      id: 305,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Loops",
      description:
        "Executing blocks of code repeatedly based on a condition or over a sequence.",
      content: `
##  Loops
### 1. The \`for\` Loop
The most common loop, used when the number of iterations is known.
\`\`\`javascript
// Structure: for (initialization; condition; increment)
for (let i = 0; i < 3; i++) {
  console.log(i); // 0, 1, 2
}
\`\`\`
### 2. The \`while\` Loop
Executes as long as a condition is \`true\`. Used when the number of iterations is unknown beforehand.
\`\`\`javascript
let count = 0;
while (count < 3) {
  console.log(count);
  count++;
}
\`\`\`
### 3. The \`do-while\` Loop
Executes the block once, then checks the condition. Guarantees the code runs at least once.
\`\`\`javascript
let x = 0;
do {
  console.log(x); // Will run once, prints 0
  x++;
} while (x > 1); // Condition is false
\`\`\`
### Loop Control
 **1.\`break\`**: Immediately exits the entire loop.
 **2.\`continue\`**: Skips the current iteration and moves to the next one.
\`\`\`javascript
for (let i = 0; i < 5; i++) {
  if (i === 2) {
    continue; // Skips printing 2
  }
  if (i === 4) {
    break; // Stops the loop entirely
  }
  console.log(i); // 0, 1, 3
}
\`\`\`
        `,
      tags: ["JavaScript", "Control Flow", "Loops", "for", "while"],
      date: "2024-10-24",
    },
    {
      id: 306,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Functions",
      description:
        "Creating reusable blocks of code using function declarations and expressions, and handling inputs and outputs.",
      content: `
## Functions
### 1. Function Declaration
A function defined with the \`function\` keyword. These are **hoisted**, meaning they can be called before they are defined in the code.
\`\`\`javascript
// Can be called before this line
function greet(name) {
  return \`Hello, \${name}!\`;
}
\`\`\`
### 2. Function Expression
A function stored in a variable. They are **not hoisted** (only the variable is).
\`\`\`javascript
// Must be defined before calling
const goodbye = function(name) {
  return \`Goodbye, \${name}!\`;
};
\`\`\`
### Parameters & Arguments
 **1.Parameters:** The names listed in the function definition (\`name\` in \`function greet(name)\`).
 **2.Arguments:** The real values passed to the function when it is called (\`"Alice"\` in \`greet("Alice")\`).
### Return Values
The \`return\` statement stops function execution and returns a value to the caller. If no \`return\` statement is present, the function implicitly returns **\`undefined\`**.
        `,
      tags: [
        "JavaScript",
        "Functions",
        "Declaration",
        "Expression",
        "Arguments",
      ],
      date: "2024-10-25",
    },
    {
      id: 307,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Arrow Functions,Rest & Spread",
      description:
        "Modern function syntax (ES6) and advanced techniques for managing function inputs.",
      content: `
##  Modern Function Features
### 1. Arrow Functions (ES6)
A compact alternative to traditional function expressions, often making the code cleaner.
 **1.Shorter Syntax:** No need for the \`function\` keyword.
 **2.Implicit Return:** If the function body is a single expression, you can omit \`return\` and the curly braces.
 **3.No \`this\` binding:** Arrow functions do not have their own \`this\`; they inherit it from the surrounding scope (lexical \`this\`).
\`\`\`javascript
// Full syntax
const add = (a, b) => { return a + b; };

// Implicit return (single expression)
const multiply = (a, b) => a * b;

// Single parameter (parentheses optional)
const square = num => num * num; 
\`\`\`
### 2. Default Parameters (ES6)
Allows formal parameters to be initialized with default values if no value or \`undefined\` is passed.
\`\`\`javascript
function log(message, level = 'info') {
  console.log(\`[\${level.toUpperCase()}]: \${message}\`);
}

log("User logged in"); // Output: [INFO]: User logged in
\`\`\`
### 3. Rest Operator (\`...\`)
Collects an indefinite number of arguments into a single array.
\`\`\`javascript
function sumAll(...numbers) {
  // numbers is an array: [1, 2, 3, 4]
  return numbers.reduce((acc, curr) => acc + curr, 0);
}
sumAll(1, 2, 3, 4); // Returns 10
\`\`\`
        `,
      tags: ["JavaScript", "Functions", "Arrow Functions", "ES6", "Rest"],
      date: "2024-10-25",
    },
    {
      id: 308,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Immediately Invoked Function Expressions",
      description:
        "Using IIFEs to create isolated scopes for code execution and prevent global pollution.",
      content: `
##  Immediately Invoked Function Expressions (IIFE)
An IIFE is a function that runs as soon as it is defined.
### Structure
An IIFE requires two main parts:
1.  **The function definition** enclosed in parentheses \`(...)\`.
2.  **The immediate invocation** using parentheses \`()\`.
\`\`\`javascript
// Structure: (function definition)(invocation);
(function() {
  var privateVariable = "I am hidden";
  console.log("IIFE ran immediately.");
})();

// privateVariable is inaccessible here, preventing global scope pollution
// console.log(privateVariable); // Error
\`\`\`
### Why Use IIFEs?
 **1.Scope Isolation (The Primary Use):** They create a private scope, protecting variables from being accessed or modified by code outside the IIFE. This is crucial for avoiding pollution of the global namespace, especially when using \`var\`.
 **2.Module Pattern:** IIFEs historically formed the backbone of the Module Pattern in JavaScript before native ES Modules were introduced.
        `,
      tags: ["JavaScript", "Functions", "IIFE", "Scope", "Patterns"],
      date: "2024-10-26",
    },
    {
      id: 309,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Scopes",
      description:
        "Detailed look at variable visibility and access rules governed by different scopes in JavaScript.",
      content: `
##  Scope in JavaScript
Scope determines the accessibility of variables, objects, and functions from different parts of the code.
### 1. Global Scope
* Variables declared outside any function or block.
* Accessible from anywhere in the program.
 **Danger:** Polluting the global scope can lead to naming conflicts.
### 2. Local Scope
Any scope defined inside a function or a block.
#### Function Scope (\`var\`)
* Variables declared with \`var\` are scoped to the nearest function.
* They are not block-scoped.
\`\`\`javascript
function exampleVar() {
  if (true) {
    var x = 10;
  }
  console.log(x); // 10 (x is visible outside the if block)
}
\`\`\`

#### Block Scope (\`let\` & \`const\`)
* Variables declared with \`let\` and \`const\` are scoped to the nearest enclosing block (defined by curly braces \`{}\`), such as \`if\` statements, \`for\` loops, or simple blocks.
\`\`\`javascript
function exampleLet() {
  if (true) {
    let y = 20;
  }
  // console.log(y); // Error: y is not defined (out of scope)
}
\`\`\`
        `,
      tags: ["JavaScript", "Scope", "Block Scope", "Global", "Local"],
      date: "2024-10-26",
    },
    {
      id: 310,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Lexical Scope and Closures",
      description:
        "Understanding how functions remember and access variables from their surrounding (parent) scope even after the parent has finished executing.",
      content: `
##  Lexical Scope
**Lexical Scope** (or Static Scope) means that the scope of a variable is determined by where it is defined in the source code, and not where it is called.
* A function is defined in a specific environment. When it's called, it searches for variables in its own scope first, then its parent's scope, and so on, up to the global scope.
##  Closures
A **closure** is the combination of a function bundled together (enclosed) with references to its surrounding state (the lexical environment).
* A function forms a closure over its parent's variables upon creation.
* This allows a function to access variables from an outer scope after the outer function has returned.
### Practical Use Case: Data Encapsulation
Closures are often used to create private variables, mimicking encapsulation in Object-Oriented Programming (OOP).
\`\`\`javascript
function createCounter() {
  let count = 0; // The private variable
  
  return function increment() { // The inner function (closure)
    count = count + 1; // Accesses count from its outer scope
    return count;
  };
}

const counter = createCounter();
console.log(counter()); // 1
console.log(counter()); // 2 
// The 'count' variable persists and is only accessible via the 'counter' function.
\`\`\`
        `,
      tags: ["JavaScript", "Scope", "Closures", "Lexical", "Encapsulation"],
      date: "2024-10-27",
    },
    {
      id: 311,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Objects",
      description:
        "Working with JavaScript's fundamental data structure for storing collections of keyed values.",
      content: `
## Creating Objects
An object is a collection of properties, where a property is an association between a name (key/property name) and a value.
### Literal Syntax (Most Common)
\`\`\`javascript
const user = {
  // key: value,
  firstName: "Alice",
  age: 25,
  isLoggedIn: true,
  'birth-year': 1999 // Keys can be strings
};
\`\`\`
### Accessing Properties
1.  **Dot Notation (Preferred):** Use when the property name is static and a valid identifier.
    \`\`\`javascript
    console.log(user.firstName); // Alice
    \`\`\`
2.  **Bracket Notation:** Use when the property name is stored in a variable or contains spaces/special characters.
    \`\`\`javascript
    const key = 'age';
    console.log(user[key]); // 25
    console.log(user['birth-year']); // 1999
    \`\`\`
##  Object Methods
A property whose value is a function is called a method.
\`\`\`javascript
const car = {
  model: 'Civic',
  // Method definition (ES6 shorthand)
  startEngine() {
    console.log(\`Engine started for \${this.model}\`);
  }
};

car.startEngine(); // Engine started for Civic
\`\`\`
        `,
      tags: ["JavaScript", "Objects", "Properties", "Methods"],
      date: "2024-10-27",
    },
    {
      id: 312,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Arrays and Basic Array Methods",
      description:
        "Working with ordered lists of data and fundamental methods for modifying them.",
      content: `
##  Arrays
Arrays are list-like objects whose prototype has methods to perform traversal and mutation operations.
### Creating Arrays
\`\`\`javascript
const colors = ['red', 'green', 'blue'];
const mixed = [1, 'two', { id: 3 }];
\`\`\`
### Accessing Elements
Arrays are zero-indexed.
\`\`\`javascript
console.log(colors[0]); // red
console.log(colors.length); // 3
\`\`\`
##  Basic Mutation Methods
These methods change (mutate) the original array.
| Method | Description | Returns |
| :--- | :--- | :--- |
| \`push(item)\` | Adds an item to the end of the array. | New length |
| \`pop()\` | Removes and returns the item from the end of the array. | The removed item |
| \`unshift(item)\` | Adds an item to the start of the array. | New length |
| \`shift()\` | Removes and returns the item from the start of the array. | The removed item |
\`\`\`javascript
let fruits = ['apple', 'banana'];
fruits.push('cherry'); // ['apple', 'banana', 'cherry']
fruits.shift(); // fruits is now ['banana', 'cherry']
\`\`\`
        `,
      tags: ["JavaScript", "Arrays", "push", "pop", "shift", "unshift"],
      date: "2024-10-28",
    },
    {
      id: 313,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Iterative Array Methods",
      description:
        "The four pillars of functional array programming for iterating, transforming, and aggregating data.",
      content: `
##  Iterative Array Methods
These methods typically take a **callback function** that runs on each array element.
### 1. \`forEach()\`
* Executes a provided function once for each array element.
 **Returns \`undefined\`**. Used for side effects (e.g., logging, DOM manipulation).
\`\`\`javascript
const nums = [1, 2, 3];
nums.forEach(n => console.log(n * 2)); // Logs 2, 4, 6
\`\`\`
### 2. \`map()\`
* Creates a new array populated with the results of calling a provided function on every element.
* Returns a new array of the same length. Used for transformation.
\`\`\`javascript
const doubled = nums.map(n => n * 2); // [2, 4, 6]
\`\`\`
### 3. \`filter()\`
* Creates a new array containing all elements that pass the test implemented by the provided function.
* Returns a new array (length <= original). Used for selection.
\`\`\`javascript
const evens = nums.filter(n => n % 2 === 0); // [2]
\`\`\`
### 4. \`reduce()\`
* Executes a reducer function on each element, resulting in a single output value.
* Takes an optional initial value. Used for aggregation.
\`\`\`javascript
const sum = nums.reduce((accumulator, current) => accumulator + current, 0); // 6
\`\`\`
        `,
      tags: ["JavaScript", "Arrays", "map", "filter", "reduce", "forEach"],
      date: "2024-10-28",
    },
    {
      id: 314,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Destructuring and Spread/Rest in Objects & Arrays",
      description:
        "Powerful ES6 features for extracting values from arrays and objects, and cloning/combining them.",
      content: `
##  Destructuring Assignment (ES6)
A JavaScript expression that makes it possible to unpack values from arrays or properties from objects into distinct variables.
### Array Destructuring
- Uses position to unpack elements.
\`\`\`javascript
const [a, b, c] = [1, 2, 3]; 
console.log(a); // 1
// You can also skip elements: const [first, , third] = arr;
\`\`\`
### Object Destructuring
- Uses property names to unpack values.
\`\`\`javascript
const user = { name: 'Bob', age: 30 };
const { name, age } = user;
console.log(name); // Bob
// You can rename properties: const { name: userName } = user;
\`\`\`
##  Spread & Rest Operators (\`...\`)
- The three dots (\`...\`) can act as either Spread or Rest, depending on context.
### 1. Spread Operator (Expansion)
- Expands an iterable (like an array or object) into its individual elements. Used for cloning or combining.
\`\`\`javascript
const arr1 = [1, 2];
const arr2 = [3, 4];
const combined = [...arr1, ...arr2]; // [1, 2, 3, 4]

const defaults = { x: 0, y: 0 };
const settings = { ...defaults, y: 10 }; // { x: 0, y: 10 } (y is overwritten)
\`\`\`
### 2. Rest Operator (Collection)
- Collects remaining elements into an array (used in function parameters) or remaining properties into an object (used in destructuring).
\`\`\`javascript
// See also section 307 for Rest in functions
const [first, ...rest] = [10, 20, 30, 40];
console.log(rest); // [20, 30, 40]

const { age, ...userData } = user; // Collects remaining properties
console.log(userData); // { name: 'Bob' }
\`\`\`
        `,
      tags: [
        "JavaScript",
        "ES6",
        "Destructuring",
        "Spread",
        "Rest",
        "Arrays",
        "Objects",
      ],
      date: "2024-10-29",
    },
    {
      id: 315,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Strings",
      description:
        "Working with textual data, modern string creation, and common manipulation methods.",
      content: `
##  Template Literals (ES6)
- Template literals (backticks \` \`) allow for easier string interpolation and multi-line strings compared to standard quotes.
### String Interpolation
- Embed expressions directly into strings using the syntax **\`\${expression}\`**.
\`\`\`javascript
const name = 'Charlie';
const greeting = \`Hello, \${name}. You are \${2024 - 1990} years old.\`;
console.log(greeting); 
// Output: Hello, Charlie. You are 34 years old.
\`\`\`
##  String Properties & Methods
- Strings are immutable; methods return a new string, they don't change the original.
| Method/Property | Description |
| :--- | :--- |
| \`length\` | Property: Returns the length of the string. |
| \`charAt(index)\` | Returns the character at a specified index. |
| \`concat(str2)\` | Joins two or more strings (Template literals are preferred). |
| \`slice(start, end)\` | Extracts a section of a string and returns a new string. |
| \`substring(start, end)\` | Similar to slice, but handles negative arguments differently. |
| \`indexOf(sub)\` | Returns the index of the first occurrence of a substring, or \`-1\`. |
| \`includes(sub)\` | Checks if a string contains a substring (returns boolean). |
| \`toUpperCase()\`/ \`toLowerCase()\` | Converts the string to upper/lower case. |
\`\`\`javascript
let text = "JavaScript is fun";
console.log(text.includes('fun')); // true
console.log(text.slice(0, 10)); // JavaScript
\`\`\`
        `,
      tags: [
        "JavaScript",
        "Strings",
        "Template Literals",
        "Interpolation",
        "Methods",
      ],
      date: "2024-10-29",
    },
    {
      id: 316,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Numbers and the Math Object",
      description:
        "Working with numeric data, parsing strings to numbers, and using the built-in Math object for advanced operations.",
      content: `
## Number Properties & Methods
### Parsing and Formatting
 **1.\`parseInt(string)\`**: Parses a string and returns an integer.
 **2.\`parseFloat(string)\`**: Parses a string and returns a floating-point number.
 **3.\`toFixed(digits)\`**: Formats a number using fixed-point notation (returns a string).
 **4.\`toPrecision(digits)\`**: Formats a number to a specified length (returns a string).
\`\`\`javascript
const price = "19.95 USD";
console.log(parseFloat(price)); // 19.95

const num = 12.3456;
console.log(num.toFixed(2)); // "12.35" (a string)
\`\`\`
### Special Number Values
 **1.\`NaN\`** (Not a Number): Result of an invalid mathematical operation (\`0/0\`, \`'a' * 5\`).
 **2.\`Infinity\`**: Result of a number divided by zero.
 **3.\`isFinite()\` / \`isNaN()\`**: Global functions to check these values.
##  Math Object
The built-in \`Math\` object has properties and methods for mathematical constants and functions.
| Method | Description |
| :--- | :--- |
| \`Math.random()\` | Returns a pseudo-random number between 0 (inclusive) and 1 (exclusive). |
| \`Math.round(x)\` | Rounds x to the nearest integer. |
| \`Math.floor(x)\` | Returns the largest integer less than or equal to x (rounds down). |
| \`Math.ceil(x)\` | Returns the smallest integer greater than or equal to x (rounds up). |
| \`Math.min(a, b, ...)\` | Returns the smallest of zero or more numbers. |
| \`Math.max(a, b, ...)\` | Returns the largest of zero or more numbers. |
| \`Math.pow(base, exp)\` | Returns the base to the exponent power. |
        `,
      tags: ["JavaScript", "Numbers", "Math", "Parsing", "Formatting"],
      date: "2024-10-30",
    },
    {
      id: 317,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Date and Time Manipulation",
      description:
        "Working with the built-in Date object for creating, manipulating, and formatting dates and times.",
      content: `
##  The Date Object
The \`Date\` object is used to work with dates and times. Dates are typically represented in milliseconds since the Epoch (January 1, 1970, 00:00:00 UTC).
### Date Object Creation
| Method | Description |
| :--- | :--- |
| \`new Date()\` | Creates a new date object with the current date and time. |
| \`new Date(year, monthIndex, ...)\` | Creates a date with specified components (monthIndex is 0-11). |
| \`new Date(milliseconds)\` | Creates a date based on a timestamp. |
| \`new Date(string)\` | Creates a date from a date string (e.g., \`"2025-01-01"\`). |
### Get Methods (Reading Values)
 **1.\`getFullYear()\`**: Gets the four-digit year.
 **2.\`getMonth()\`**: Gets the month (0 for January, 11 for December).
 **3.\`getDate()\`**: Gets the day of the month (1-31).
 **4.\`getDay()\`**: Gets the day of the week (0 for Sunday, 6 for Saturday).
 **5.\`getHours()\` / \`getMinutes()\` / \`getSeconds()\`**.
### Set Methods (Modifying Values)
* \`setFullYear(year)\`
* \`setMonth(monthIndex)\`
* \`setDate(day)\`
### Formatting and Comparing Dates
 **1.\`toString()\`, \`toDateString()\`, \`toLocaleDateString()\`**: Convert the Date object to various string formats.
 **2.Comparison:** Dates can be compared using standard comparison operators (\`<\`, \`>\`) because they are internally numeric (timestamps).
\`\`\`javascript
const today = new Date();
const nextYear = new Date(today.getFullYear() + 1, 0, 1);
console.log(nextYear.toLocaleDateString()); // Formatted based on locale
\`\`\`
        `,
      tags: ["JavaScript", "Date", "Time", "Formatting"],
      date: "2024-10-30",
    },
    {
      id: 318,
      topic: "javascript",
      seriesKey: "javascript",
      title: "DOM Manipulation",
      description:
        "Using JavaScript to interact with and dynamically modify the Document Object Model (DOM) of a web page.",
      content: `
##  DOM Manipulation
The **DOM (Document Object Model)** is a programming interface for web documents. It represents the page so that programs can change the document structure, style, and content.
### 1. Selecting Elements
| Method | Description | Returns |
| :--- | :--- | :--- |
| \`getElementById('id')\` | Finds the first element with the specified ID. | Single Element |
| \`querySelector('selector')\` | Finds the first element matching a CSS selector. | Single Element |
| \`querySelectorAll('selector')\` | Finds all elements matching a CSS selector. | NodeList |
\`\`\`javascript
const header = document.querySelector('h1');
const buttons = document.querySelectorAll('.btn');
\`\`\`
### 2. Changing Content
 - \`innerHTML\`: Gets or sets the HTML content (including tags). Caution: Prone to XSS attacks.
 - \`textContent\`: Gets or sets the plain text content (safer).
 - \`value\`: Gets or sets the content of form elements (\`<input>\`, \`<textarea>\`).
\`\`\`javascript
header.textContent = "New Title";
\`\`\`
### 3. Changing Styles and Classes
 **1.Inline Styles:**
    \`\`\`javascript
    header.style.color = 'blue'; // CSS properties are camelCase
    \`\`\`
 **2.Classes:** The preferred method.
    \`\`\`javascript
    header.classList.add('active');
    header.classList.remove('inactive');
    header.classList.toggle('highlight');
    \`\`\`
        `,
      tags: ["JavaScript", "DOM", "Manipulation", "Selection", "Classes"],
      date: "2024-10-31",
    },
    {
      id: 319,
      topic: "javascript",
      seriesKey: "javascript",
      title: "DOM Manipulation",
      description:
        "Creating new elements and dynamically managing the structure of the DOM tree.",
      content: `
##  Modifying the DOM Structure
### 1. Creating a New Element
The \`createElement()\` method creates a new HTML element node.
\`\`\`javascript
// Create a new <p> tag
const newParagraph = document.createElement('p');
newParagraph.textContent = "This was dynamically added!";
newParagraph.classList.add('dynamic-text');
\`\`\`
### 2. Adding Elements (Appending)
* \`parentElement.appendChild(childElement)\`: Adds a node to the end of the parent's list of children.
* \`parentElement.append(element1, element2, ...)\`: A newer method that can append multiple nodes and strings.
\`\`\`javascript
const container = document.getElementById('main-content');
container.appendChild(newParagraph);
\`\`\`
### 3. Removing Elements
* \`parentElement.removeChild(childElement)\`: Requires knowing the parent.
* \`childElement.remove()\`: The simplest and preferred way (supported in modern browsers).
\`\`\`javascript
// Remove the paragraph itself
newParagraph.remove(); 
\`\`\`
### 4. Replacing Elements
* \`parentElement.replaceChild(newChild, oldChild)\`: Replaces one child node with another.
        `,
      tags: ["JavaScript", "DOM", "Manipulation", "createElement", "append"],
      date: "2024-10-31",
    },
    {
      id: 320,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Events: Handling,Object, and Delegation",
      description:
        "Responding to user and browser actions using event handlers and mastering event flow.",
      content: `
##  Events in JavaScript
Events are actions or occurrences that happen in the system you are programming, which the system tells you about so you can respond.
### 1. Adding Event Listeners
The preferred method is **\`addEventListener()\`**.
\`\`\`javascript
const button = document.getElementById('myButton');

// Syntax: element.addEventListener(event, handler, useCapture);
button.addEventListener('click', function(event) {
  console.log("Button was clicked!");
  event.preventDefault(); // Stops the default browser action (e.g., form submission)
});

// To remove: button.removeEventListener('click', handlerReference);
\`\`\`
### 2. The Event Object
The handler function receives an **Event object** as its first argument, containing crucial information.
 **1.\`event.target\`**: The element where the event originally occurred.
 **2.\`event.currentTarget\`**: The element the listener was attached to.
 **3.\`event.type\`**: The name of the event (\`'click'\`, \`'submit'\`).
 **4.\`event.preventDefault()\`**: Cancels the default action of the event (e.g., prevents a link from navigating).
 **5.\`event.stopPropagation()\`**: Stops the event from propagating further in the DOM tree.
### 3. Event Types
Common types include \`click\`, \`mouseover\`, \`keydown\` (keyboard), \`submit\` (form), and \`input\` (form value change).
        `,
      tags: [
        "JavaScript",
        "Events",
        "DOM",
        "addEventListener",
        "preventDefault",
      ],
      date: "2024-11-01",
    },
    {
      id: 321,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Event Flow",
      description:
        "Understanding the phases of event propagation and how to use delegation to efficiently manage events.",
      content: `
##  Event Flow
When an event occurs on an element, it goes through three phases in the DOM tree:
### 1. Capturing Phase
The event travels **down** from the window/document to the target element. Listeners attached with \`useCapture: true\` run during this phase.
### 2. Target Phase
The event reaches the target element.
### 3. Bubbling Phase (Default)
The event travels **up** from the target element back to the window/document. This is the default behavior for most events.
\`\`\`javascript
// Bubbling is the default (false)
document.getElementById('child').addEventListener('click', handler, false); 
\`\`\`
##  Event Delegation
Event Delegation is the technique of adding a single event listener to a common parent element, instead of adding listeners to multiple individual child elements.
### Advantages:
 **1.Performance:** Fewer event listeners are attached, saving memory.
 **2.Dynamic Content:** Automatically handles events for elements that are added to the DOM dynamically after the page loads.
### Implementation:
1.  Attach the listener to the **parent**.
2.  Use **\`event.target\`** inside the handler to identify *which* child element triggered the event.
\`\`\`javascript
document.getElementById('list').addEventListener('click', function(e) {
  // Check if the clicked element is an <li> (or other specific child)
  if (e.target.tagName === 'LI') {
    console.log("Clicked item:", e.target.textContent);
  }
});
\`\`\`
        `,
      tags: ["JavaScript", "Events", "Bubbling", "Capturing", "Delegation"],
      date: "2024-11-01",
    },
    {
      id: 322,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Functions: Call, Apply, and Bind",
      description:
        "Methods for explicitly setting the execution context ('this' value) of a function.",
      content: `
##  Setting Execution Context ('this')
Every function in JavaScript has built-in methods: \`call()\`, \`apply()\`, and \`bind()\` which are used to control the function's \`this\` value.
### The \`this\` Keyword
*The value of this is determined by *how* a function is called, not where it is defined (unless it's an Arrow Function).
### 1. \`call(thisArg, arg1, arg2, ...)\`
Invokes the function immediately, setting its \`this\` value to \`thisArg\`. Arguments are passed individually.
\`\`\`javascript
function describe() {
  console.log(\`I am a \${this.type} named \${this.name}\`);
}

const obj = { name: 'Fido', type: 'dog' };
describe.call(obj); // I am a dog named Fido
\`\`\`
### 2. \`apply(thisArg, [argsArray])\`
Identical to \`call()\`, but accepts the function arguments as a **single array**.
\`\`\`javascript
function sum(a, b) { return a + b; }
sum.apply(null, [5, 3]); // 8 (null/undefined for 'this' defaults to global/window in non-strict mode)
\`\`\`
### 3. \`bind(thisArg, arg1, ...)\`
Does **not** invoke the function immediately. Instead, it returns a **new function** permanently bound to the specified \`thisArg\` and any initial arguments (partial application/currying).
\`\`\`javascript
const obj = { x: 5 };
function getX() { return this.x; }

const boundGetX = getX.bind(obj);
console.log(boundGetX()); // 5 
\`\`\`
        `,
      tags: [
        "JavaScript",
        "Advanced Functions",
        "this",
        "call",
        "apply",
        "bind",
      ],
      date: "2024-11-02",
    },
    {
      id: 323,
      topic: "javascript",
      seriesKey: "javascript",
      title: " Higher-Order Functions and Callbacks",
      description:
        "Understanding functions as first-class citizens and using them to create flexible and reusable code.",
      content: `
##  Functions as First-Class Citizens
In JavaScript, functions are treated like any other variable:
1.  They can be assigned to a variable.
2.  They can be passed as an argument to another function.
3.  They can be returned as the result of another function.
##  Higher-Order Functions (HOF)
A HOF is a function that either:
* Takes one or more functions as arguments.
* Returns a function as its result.
### Example: \`map\` (A Common HOF)
- \`map()\` takes a callback function as an argument to transform each element of an array.
\`\`\`javascript
const numbers = [1, 2, 3];
const square = num => num * num; // Callback function
const squared = numbers.map(square); // map is the HOF
\`\`\`
##  Callback Functions
A function passed into another function as an argument, which is then invoked inside the outer function to complete some kind of routine or action.
### Common Use Cases:
 **1.Asynchronous Operations:** Executed when a task (like data fetching or a timeout) is complete.
 **2.Event Handlers:** Executed when a specific event (like a click) occurs.
 **3.Functional Array Methods:** The function passed to \`map\`, \`filter\`, \`reduce\`.
        `,
      tags: ["JavaScript", "Advanced Functions", "HOF", "Callback"],
      date: "2024-11-02",
    },
    {
      id: 324,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Asynchronous Code: Promises and Timers",
      description:
        "Fundamentals of handling operations that don't complete immediately, such as timeouts and I/O operations.",
      content: `
##  Asynchronous JavaScript
Asynchronous (Async) operations allow the program to start a long-running task and continue to execute other code instead of waiting for the task to finish.
### 1. \`setTimeout\` & \`setInterval\` (Timers)
 1.**\`setTimeout(callback, delay)\`**: Executes a function once after a specified delay (in milliseconds). Returns a timer ID.
 2.**\`setInterval(callback, interval)\`**: Executes a function repeatedly at every specified interval. Returns a timer ID.
 3.**\`clearTimeout(id)\` / \`clearInterval(id)\`**: Used to stop the execution of the timer.
\`\`\`javascript
console.log('Start');
setTimeout(() => {
  console.log('Delayed by 1 second');
}, 1000);
console.log('End'); 
// Output: Start -> End -> Delayed by 1 second
\`\`\`
### 2. Promises (Introduction)
A **Promise** is an object representing the eventual completion or failure of an asynchronous operation.
 **States:** A Promise is always in one of three states:
    1.  **Pending:** Initial state, neither fulfilled nor rejected.
    2.  **Fulfilled (Resolved):** The operation completed successfully.
    3.  **Rejected:** The operation failed.
* Promises avoid Callback Hell by chaining operations using \`.then()\` and handling errors with \`.catch()\`.
        `,
      tags: [
        "JavaScript",
        "Asynchronous",
        "Promises",
        "setTimeout",
        "setInterval",
      ],
      date: "2024-11-03",
    },
    {
      id: 325,
      topic: "javascript",
      seriesKey: "javascript",
      title: "ES6 and Modern JavaScript: Syntax and Features",
      description:
        "A summary of key features introduced in ECMAScript 2015 (ES6) that modernized JavaScript development.",
      content: `
##  ES6 (ECMAScript 2015) Features
ES6 was the biggest update to JavaScript, fundamentally changing how developers write code.
| Feature | Description | Example |
| :--- | :--- | :--- |
| \`let\` & \`const\` | Block-scoped variable declarations. | \`const x = 10; let y = 5;\` |
| Template Literals | Strings with backticks, allowing interpolation and multiline support. | \`\`Hello \${name}\`\` |
| Arrow Functions | Compact syntax, lexical \`this\` binding. | \`const sum = (a, b) => a + b;\` |
| Destructuring | Conveniently unpack values from arrays or properties from objects. | \`const { name } = user;\` |
| Spread/Rest | Used for array/object copying and combining, and collecting function arguments. | \`const newArr = [...oldArr, 4];\` |
| Default Parameters | Provide default values if arguments are missing. | \`function f(x=1) {}\` |
| *Enhanced Object Literals | Shorthand for property names and method definitions. | \`{ user, greet() {} }\` |
| Modules | Native support for dividing code into separate files using \`import\` and \`export\`. | \`import { f } from './utils';\` |
 **Impact:** ES6 features are now standard and considered best practice in modern JavaScript development.
        `,
      tags: [
        "JavaScript",
        "ES6",
        "Modern JavaScript",
        "let",
        "Arrow Functions",
      ],
      date: "2024-11-03",
    },
    {
      id: 326,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Advanced Arrays and Collections",
      description:
        "Exploring advanced array methods and the specialized collection types: Set and Map.",
      content: `
##  Advanced Array Methods
### 1. \`find()\` & \`findIndex()\`
1. **\`find(callback)\`**: Returns the **first** element that satisfies the testing function, or \`undefined\` if none is found.
2. **\`findIndex(callback)\`**: Returns the **index** of the first element that satisfies the testing function, or \`-1\` if none is found.
\`\`\`javascript
const arr = [{id: 1, name: 'A'}, {id: 2, name: 'B'}];
const result = arr.find(item => item.id === 2); // {id: 2, name: 'B'}
\`\`\`
### 2. \`flat()\` & \`flatMap()\`
 1.**\`flat(depth)\`**: Creates a new array with all sub-array elements recursively concatenated up to the specified depth.
 2.**\`flatMap(callback)\`**: Combines \`map()\` and \`flat(1)\` into a single, efficient method.
##  Collections
### 1. \`Set\`
* A collection of unique values. Duplicate values are ignored.
* Used to quickly remove duplicates from an array.
\`\`\`javascript
const unique = new Set([1, 1, 2, 3, 3]); // Set {1, 2, 3}
\`\`\`
### 2. \`Map\`
* A collection of key/value pairs where keys can be of any data type (unlike Object keys, which are restricted to strings/Symbols).
* Maintains the insertion order.
        `,
      tags: ["JavaScript", "Arrays", "Set", "Map", "flat", "find"],
      date: "2024-11-04",
    },
    {
      id: 327,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Advanced Objects: Keys, Values, and Mutability Control",
      description:
        "Methods for inspecting and controlling object properties, including iteration and immutability utilities.",
      content: `
##  Object Inspection and Iteration
| Method | Description | Returns |
| :--- | :--- | :--- |
| \`Object.keys(obj)\` | Returns an array of the object's own property names (keys). | Array of Strings |
| \`Object.values(obj)\` | Returns an array of the object's own property values. | Array of Values |
| \`Object.entries(obj)\` | Returns an array of [key, value] pairs. | Array of Arrays |
\`\`\`javascript
const user = { id: 1, name: 'John' };
Object.entries(user); // [['id', 1], ['name', 'John']]
\`\`\`
##  Object Mutability Control
These methods control how an object can be changed, often used for enforcing immutability.
### 1. \`Object.assign(target, source, ...)\`
* Copies all enumerable properties from one or more source objects to a target object.
* Used for merging objects or shallow cloning.
### 2. \`Object.freeze(obj)\`
* Prevents new properties from being added to it, prevents existing properties from being removed, and prevents existing properties from being changed (read-only).
* Creates shallow immutability.
### 3. \`Object.seal(obj)\`
* Prevents new properties from being added and marks all existing properties as non-configurable.
* Allows existing properties to be changed (values can be updated).
        `,
      tags: [
        "JavaScript",
        "Objects",
        "Object.keys",
        "Object.freeze",
        "Mutability",
      ],
      date: "2024-11-04",
    },
    {
      id: 328,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Prototypes and Inheritance",
      description:
        "Understanding the prototype chain, the fundamental mechanism for inheritance and property sharing in JavaScript.",
      content: `
##  Prototypes and Inheritance
JavaScript is a **prototype-based** language, meaning behavior reuse (inheritance) is achieved through a mechanism called the prototype chain.
### The Prototype
* Every JavaScript object has a private property called prototype (exposed as \`[[Prototype]]\`), which is a link to another object.
* This linked object is the prototype of the first object.
### The Prototype Chain
When you try to access a property or method on an object:
1.  JavaScript first looks at the object itself.
2.  If it doesn't find the property, it looks at the object's prototype.
3.  If still not found, it looks at the prototype's prototype, and so on, until it reaches the end of the chain (which is \`Object.prototype\`).
### Example
\`\`\`javascript
const animal = {
  type: 'Mammal',
  makeSound: function() { console.log('Noise'); }
};

// Set 'animal' as the prototype of 'dog'
const dog = Object.create(animal); 
dog.name = 'Rex';

console.log(dog.type); // Mammal (inherited from 'animal')
dog.makeSound(); // Noise (inherited from 'animal')

// dog's prototype is 'animal'. animal's prototype is Object.prototype.
\`\`\`
 **Classes (ES6):** The \`class\` keyword is syntactic sugar over the prototype chain mechanism.
        `,
      tags: [
        "JavaScript",
        "Objects",
        "Prototypes",
        "Inheritance",
        "Prototype Chain",
      ],
      date: "2024-11-05",
    },
    {
      id: 329,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Error Handling",
      description:
        "Strategies for catching runtime errors, gracefully recovering, and throwing custom error messages.",
      content: `
##  Error Handling
The **\`try...catch\`** statement is used to handle runtime JavaScript errors (exceptions) without stopping the program.
### 1. \`try...catch...finally\`
1. **\`try\`**: Contains the code block where you anticipate an error might occur.
2. **\`catch(error)\`**: Contains the code block to be executed if an error occurs in the \`try\` block. The error object provides details.
3. **\`finally\`**: Contains the code block that is executed regardless of whether an error occurred or was caught. (Optional)
\`\`\`javascript
try {
  const result = value / 0; // Throws an error in some contexts (but usually returns Infinity)
  nonExistentFunction(); // Will throw a ReferenceError
} catch (e) {
  console.error("An error occurred:", e.message);
} finally {
  console.log("This always runs.");
}
\`\`\`
### 2. \`throw new Error()\`
You can manually throw an exception to interrupt program execution and trigger a \`catch\` block.
\`\`\`javascript
function checkAge(age) {
  if (age < 0) {
    throw new Error("Age cannot be negative.");
  }
  return age;
}
\`\`\`
### 3. Custom Error Handling
You can create more specific error types by extending the built-in \`Error\` class (for modern ES6 code) or by creating a custom constructor function (older method).
\`\`\`javascript
class NetworkError extends Error {
  constructor(message) {
    super(message);
    this.name = "NetworkError"; // Custom error name
  }
}
\`\`\`
        `,
      tags: ["JavaScript", "Error Handling", "try", "catch", "throw"],
      date: "2024-11-05",
    },
    {
      id: 330,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Asynchronous JavaScript: Promises",
      description:
        "Deep dive into the Promise object structure for managing the success and failure states of async operations.",
      content: `
##  Promises
A Promise is an object that represents a value that may not be available yet, but will be resolved at some point in the future.
### Promise Constructor
A new Promise takes a function called the **executor**, which itself takes two callback functions: \`resolve\` and \`reject\`.
\`\`\`javascript
const myPromise = new Promise((resolve, reject) => {
  // Simulate an async operation (e.g., fetching data)
  setTimeout(() => {
    const success = true;
    if (success) {
      resolve("Operation successful!"); // Fulfills the Promise
    } else {
      reject(new Error("Operation failed.")); // Rejects the Promise
    }
  }, 1000);
});
\`\`\`
### Consuming Promises
1. **\`.then(onFulfilled, onRejected)\`**: Used to attach callbacks for both the fulfilled and rejected states.
2. **\`.catch(onRejected)\`**: A cleaner way to handle rejection; equivalent to \`.then(null, onRejected)\`.
3. **\`.finally(onFinally)\`**: Executes a callback when the Promise is settled (resolved or rejected).
\`\`\`javascript
myPromise
  .then(result => {
    console.log(result); // Runs on resolve
    return \`Processed: \${result}\`; // Passes value to the next .then
  })
  .catch(error => {
    console.error(error.message); // Runs on reject
  })
  .finally(() => {
    console.log("Promise finished.");
  });
\`\`\`
        `,
      tags: ["JavaScript", "Asynchronous", "Promises", "then", "catch"],
      date: "2024-11-06",
    },
    {
      id: 331,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Asynchronous JavaScript: Async/Await and Fetch API",
      description:
        "Mastering modern asynchronous syntax and the standard API for making network requests (AJAX).",
      content: `
## Async/Await: Modern Asynchronicity
**Async/Await** is syntax built on top of Promises to make asynchronous code look and behave synchronously.
### The \`async\` & \`await\` Keywords
* The \`async\` keyword is used to declare an asynchronous function. An \`async\` function always returns a Promise.
* The \`await\` keyword is used to pause the execution of an \`async\` function until a Promise is resolved or rejected. \`await\` can only be used inside an \`async\` function.
### Fetch API
The **Fetch API** provides a modern, promise-based interface for making network requests (e.g., getting data from a REST API).
### Example: Data Fetching with Async/Await
\`\`\`javascript
async function getPost(id) {
    try {
        // Pauses execution until response header is received
        const response = await fetch(\`/api/posts/\${id}\`);
        
        if (!response.ok) {
          throw new Error(\`HTTP error! Status: \${response.status}\`);
        }
        
        // Pauses execution until JSON parsing completes
        const data = await response.json(); 
        
        return data;
    } catch (error) {
        console.error("Fetch error:", error);
        throw error; // Rejects the Promise returned by the async function
    }
}
\`\`\`
        `,
      tags: [
        "JavaScript",
        "Asynchronous",
        "Async/Await",
        "Promises",
        "Fetch API",
        "AJAX",
      ],
      date: "2024-11-06",
    },
    {
      id: 332,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Modules and Tooling",
      description:
        "Structuring code using modular systems and the role of modern build tools like Webpack and Parcel.",
      content: `
##  JavaScript Modules
### 1. ES Modules (ESM)
The modern, standardized modular system in JavaScript. Used in browsers and Node.js (with the \`.mjs\` extension or \`"type": "module"\` in \`package.json\`).
1. **\`export\`**: Used to make variables, functions, or classes available outside the module.
2. **\`import\`**: Used to bring exports into the current module's scope.
\`\`\`javascript
// utils.js
export const PI = 3.14; 
export function sum(a, b) { return a + b; } 

// main.js
import { PI, sum } from './utils.js'; // Named import
// import * as Utils from './utils.js'; // Namespace import
\`\`\`
### 2. CommonJS
The traditional module system used by **Node.js**. Uses \`require\` for importing and \`module.exports\` for exporting.
\`\`\`javascript
// utils.js (CommonJS)
module.exports = {
  sum: (a, b) => a + b
};

// main.js (CommonJS)
const utils = require('./utils');
utils.sum(1, 2);
\`\`\`
##  Bundlers
Tools like **Webpack**, **Parcel**, and **Vite** compile and package all modules, assets (CSS, images), and dependencies into a single or small set of optimized files (bundles) for deployment. This is necessary because browsers can't natively handle all module types or features.
        `,
      tags: ["JavaScript", "Modules", "ESM", "CommonJS", "Bundlers", "Node.js"],
      date: "2024-11-07",
    },
    {
      id: 333,
      topic: "javascript",
      seriesKey: "javascript",
      title:
        "Browser Storage and APIs",
      description:
        "Using the browser's persistent storage mechanisms and core web APIs for modern application development.",
      content: `
##  Browser Storage
The browser provides storage mechanisms that allow web applications to store data client-side.
| Storage | Data Type | Persistence | Accessibility | Capacity |
| :--- | :--- | :--- | :--- | :--- |
| LocalStorage | Key/Value (Strings) | Permanent (until user clears) | Same origin (domain) | ~5MB per domain |
| SessionStorage | Key/Value (Strings) | Session-based (cleared when tab closes) | Same origin (domain) | ~5MB per domain |
| Cookies | Key/Value (Strings) | Expiration date | Same origin + can be sent to server | ~4KB per domain |
### LocalStorage/SessionStorage Methods
1. **\`setItem(key, value)\`**: Store a value.
2. **\`getItem(key)\`**: Retrieve a value.
3. **\`removeItem(key)\`**: Remove an item.
4. **\`clear()\`**: Clear all items.
\`\`\`javascript
localStorage.setItem('username', 'Alice');
const user = localStorage.getItem('username'); // Alice
\`\`\`
## 🌍 Browser APIs
1. **Geolocation API:** Allows access to the user's geographical location after consent.
2. **Fetch API:** The standard, promise-based mechanism for making network requests (covered in 331).
3. **IndexedDB (Concept):** A low-level API for large-scale storage of structured data client-side (often > 5MB).
        `,
      tags: ["JavaScript", "APIs", "LocalStorage", "SessionStorage", "Fetch"],
      date: "2024-11-07",
    },
    {
      id: 334,
      topic: "javascript",
      seriesKey: "javascript",
      title: "JavaScript Patterns",
      description:
        "Classical design patterns used to structure and organize JavaScript code for better maintainability and encapsulation.",
      content: `
##  JavaScript Design Patterns
### 1. Module Pattern
Uses the power of **Closures** (often an IIFE) to create privacy and encapsulate state, allowing only a public interface to be exposed. This protects implementation details.
\`\`\`javascript
const Calculator = (function() {
  // Private variable (closure)
  let total = 0; 
  
  // Public interface definition
  return {
    add: (a) => { total += a; },
    getTotal: () => total
  };
})();

Calculator.add(5);
// console.log(Calculator.total); // undefined (total is private)
\`\`\`
### 2. Revealing Module Pattern
A variation of the Module Pattern where all private members are defined first, and the public interface is returned as an object literal that references the private members. This makes it easier to see what is exposed.
### 3. Singleton Pattern
Restricts the instantiation of a class or object to a single instance. This is useful for things like managing configuration settings, a logging service, or a central state store.
 **Implementation:** Typically uses an IIFE to hold a single instance, only creating it the first time it is requested.
        `,
      tags: ["JavaScript", "Patterns", "Module", "Singleton", "IIFE"],
      date: "2024-11-08",
    },
    {
      id: 335,
      topic: "javascript",
      seriesKey: "javascript",
      title: " Hoisting, Scope Chain, and 'this'",
      description:
        "A deep dive into core JavaScript engine concepts that explain unusual runtime behaviors.",
      content: `
##  Core Engine Concepts
### 1. Hoisting
Hoisting is the JavaScript behavior where variable and function declarations are conceptually moved to the top of their containing scope during the compilation phase.
1. **Function Declarations:** The entire function is hoisted, so you can call it before it appears in the code.
2. **\`var\` Declarations:** Only the declaration is hoisted (initialized to \`undefined\`), not the assignment.
3. **\`let\` / \`const\`:** Their declarations are also hoisted, but they remain uninitialized until the line of assignment, creating the **Temporal Dead Zone (TDZ)**. Accessing them in the TDZ throws an error.
### 2. Scope Chain
When JavaScript executes a function, it creates an execution context. If a variable is not found in the current scope, the JavaScript engine looks up the **Scope Chain**—a sequence of outer scopes—until it finds the variable or reaches the global scope.
### 3. \`this\` Keyword
The value of \`this\` is the object that currently owns the executing code. Its value is determined at the **time of function execution**, not definition.
| Call Context | \`this\` Value |
| :--- | :--- |
| Global/Simple Function | Global object (\`window\` in browsers, \`undefined\` in strict mode) |
| Object Method | The object that the method is on. |
| Constructor (\`new\` keyword) | The newly created instance. |
| Arrow Function | Lexically inherited from the surrounding scope. (Cannot be bound). |
        `,
      tags: ["JavaScript", "Advanced", "Hoisting", "Scope Chain", "this"],
      date: "2024-11-08",
    },
    {
      id: 336,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Advanced Topics: Event Loop, Call Stack, and Memory Management",
      description:
        "Understanding how JavaScript handles concurrency, memory allocation, and the non-blocking nature of asynchronous tasks.",
      content: `
##  Concurrency Model
### 1. Call Stack
* A LIFO (Last-In, First-Out) stack that tracks all currently executing functions.
* JavaScript is single-threaded, meaning only one function can be on the stack and running at a time. Blocking the stack (e.g., with long, synchronous loops) freezes the application.
### 2. Event Loop
The mechanism that handles concurrency. It constantly checks two things:
1.  **The Call Stack:** Is it empty?
2.  **The Task Queue:** Are there any pending, completed asynchronous tasks (like a resolved Promise or a finished \`setTimeout\`) waiting?
If the stack is empty, the Event Loop pushes the first item from the Task Queue onto the Call Stack to be executed.
### 3. Memory Management
1. **Garbage Collection:** JavaScript performs automatic memory management. When an object is no longer reachable (referenced) by the code, the garbage collector identifies it as "garbage" and reclaims the memory.
2. **Closures & Memory:** Closures can inadvertently hold onto variables longer than needed, potentially causing memory leaks if not managed correctly.
3. **Non-Blocking:** Async tasks (like Fetch, setTimeout) are delegated to the browser/Node.js environment, keeping the Call Stack free and the application responsive.
        `,
      tags: ["JavaScript", "Advanced", "Event Loop", "Call Stack", "Memory"],
      date: "2024-11-09",
    },
    {
      id: 337,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Advanced Topics: Debouncing, Throttling, and Currying",
      description:
        "Performance optimization techniques for rate-limiting function calls and advanced functional programming concepts.",
      content: `
##  Optimization and Functional Concepts
### 1. Debouncing
Ensures that a function is executed only **after** a specified delay following the last time it was invoked.
- Use Case: Limiting how often a costly function runs (e.g., handling rapid input events, like typing in a search bar). You only want to make the API call after the user stops typing for 300ms.
\`\`\`javascript
// Simplified concept
function debounce(fn, delay) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId); // Clear previous timer
    timeoutId = setTimeout(() => { // Set a new timer
      fn.apply(this, args);
    }, delay);
  };
}
\`\`\`
### 2. Throttling
Ensures that a function is executed at most once every specified period.
* Use Case: Limiting the rate of continuous events (e.g., handling scroll or resize events). You want the handler to run regularly, but not hundreds of times per second.
### 3. Currying
The process of transforming a function that takes multiple arguments into a sequence of functions, each taking a single argument.
\`\`\`javascript
// Normal function
const add = (a, b) => a + b;

// Curried function
const curriedAdd = (a) => (b) => a + b;

curriedAdd(5)(3); // 8
const addFive = curriedAdd(5); // Returns the (b) => 5 + b function
addFive(10); // 15
\`\`\`
        `,
      tags: [
        "JavaScript",
        "Advanced",
        "Debouncing",
        "Throttling",
        "Currying",
        "Performance",
      ],
      date: "2024-11-09",
    },
    {
      id: 338,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Functional Programming Concepts",
      description:
        "Exploring the functional paradigm with concepts like pure functions, immutability, and higher-order functions.",
      content: `
##  Functional Programming (FP)
A programming paradigm that treats computation as the evaluation of mathematical functions and avoids changing state and mutable data.
### 1. Pure Functions
A function is **pure** if it adheres to two rules:
1.  **Deterministic:** Given the same inputs, it always returns the same output.
2.  **No Side Effects:** It does not modify any state outside of its local scope (e.g., no changing global variables, no I/O operations, no DOM mutation).
### 2. Immutability
Data should not be changed after it is created. Instead of modifying an existing object or array, you create a new one with the necessary changes.
* How to achieve: Use \`const\` for variable assignment, and use methods like \`map()\`, \`filter()\`, \`reduce()\`, and the Spread Operator (\`...\`) instead of mutable methods (\`push()\`, direct assignment).
### 3. Higher-Order Functions (HOF)
Functions that take functions as arguments or return functions (covered in 323). HOFs like \`map\`, \`filter\`, and \`reduce\` are central to FP in JavaScript.
### Benefits of FP
1. **Predictability:** Pure functions are easy to reason about and test.
2. **Maintainability:** Less reliance on shared, mutable state makes code easier to maintain and debug.
3. **Concurrency:** Immutable data structures simplify concurrent or parallel execution.
        `,
      tags: [
        "JavaScript",
        "Advanced",
        "Functional Programming",
        "Pure Functions",
        "Immutability",
      ],
      date: "2024-11-10",
    },
    {
      id: 339,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Modern JavaScript Ecosystem: Node.js and npm",
      description:
        "Understanding the server-side runtime and the world's largest software registry for package management.",
      content: `
##  Node.js and npm
### 1. Node.js (Runtime)
1. **What it is:** A cross-platform, open-source JavaScript **runtime environment** that runs the V8 JavaScript engine outside of a browser.
2. **Non-Blocking I/O:** Built around an event-driven, non-blocking I/O model (Event Loop), making it highly efficient for building scalable network applications (backends, APIs).
3. **Use Cases:** Web servers, command-line tools, scripting, build tooling.
### 2. npm (Node Package Manager)
1. **What it is:** The default package manager for Node.js, and the world's largest registry of software libraries (packages/modules).
2. **\`package.json\`:** A manifest file located in the root directory of a project that lists the project's metadata, dependencies, and script commands.
2. **\`node_modules\`:** The folder where npm downloads and installs the dependencies required by the project.
### Key npm Commands
1. **\`npm init\`**: Creates a new \`package.json\` file.
2. **\`npm install <package-name>\`**: Installs a package as a dependency.
3. **\`npm install --save-dev <package-name>\`**: Installs a package as a development dependency (used for testing, bundling, etc.).
4. **\`npm start\` / \`npm run <script-name>\`**: Executes custom scripts defined in \`package.json\`.
        `,
      tags: ["JavaScript", "Ecosystem", "Node.js", "npm", "Tooling"],
      date: "2024-11-10",
    },
    {
      id: 340,
      topic: "javascript",
      seriesKey: "javascript",
      title: "Modern JavaScript Ecosystem: Build Tools and Frameworks",
      description:
        "The role of Babel, Webpack, and modern meta-frameworks in the JavaScript build pipeline.",
      content: `
##  Build Tools

### 1. Babel
1. **What it is:** A **JavaScript compiler** (transpiler) that converts modern JavaScript code (like ES6+) into backwards-compatible versions that can be run by older browsers or environments.
2. **Role:** Enables developers to use the latest features without worrying about immediate browser support.
###  Webpack / Vite
1. **Webpack:** A powerful, highly configurable **module bundler**. It takes all your project assets (JS, CSS, images) and packages them into bundles optimized for the browser.
2. **Vite:** A newer, faster build tool that leverages native ES Modules (ESM) in the browser during development, allowing for extremely fast cold starts and hot module replacement (HMR). It uses Rollup for production builds.
##  Frontend Frameworks
Frontend frameworks provide structure, optimization, and a component-based model for building complex user interfaces.
1. **React:** A library for building user interfaces, known for its component model and Virtual DOM.
2. **Vue:** A progressive framework known for its simplicity and gentle learning curve.
3. **Angular:** A comprehensive, opinionated framework often used for large-scale enterprise applications.
4. **The Bridge:** These frameworks integrate with JavaScript's core concepts (ES6, modules, asynchronous code) but abstract away direct DOM manipulation.
        `,
      tags: [
        "JavaScript",
        "Ecosystem",
        "Babel",
        "Webpack",
        "Vite",
        "Frameworks",
      ],
      date: "2024-11-11",
    },
];
